import copy from "copy-to-clipboard";
import { useEffect, useRef, useState } from "react";

import McpIntegrationDialog from "@apps/account/components/integrations/mcp-integration-dialog";
import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

import {
  AI_TOOLS,
  ReviewForAiTool,
  getAiToolUrl,
  getReviewAiToolPrompt,
} from "./review-ai-tool";

/**
 * The other way through a review: in the reviewer's own AI tool, which reads
 * the questions and saves the answers back here over Topicflow's MCP. The
 * prompt is ready to copy or to open in the tool of their choice.
 */
const McpHandoffSection = ({
  review: reviewForAiTool,
  onDone,
}: {
  review: ReviewForAiTool;
  /** Once the prompt has gone to a tool, e.g. to close the modal it sits in. */
  onDone: () => void;
}) => {
  const label = useLabel();
  const [mcpSetupOpen, setMcpSetupOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);
  const prompt = getReviewAiToolPrompt(
    reviewForAiTool,
    label("review"),
    window.location.origin,
  );

  const handleCopy = () => {
    if (!copy(prompt)) {
      errorNotificationVar({
        title: "Select the prompt and copy it",
        description: "Your browser blocked clipboard access.",
      });
      return;
    }
    successNotificationVar({
      title: "Prompt copied",
      description: "Paste it into your AI tool to start.",
    });
    // The prompt stays in view, so Copy says it worked and the modal stays
    // open for opening a tool too.
    setCopied(true);
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
  };

  const openIn = (tool: (typeof AI_TOOLS)[number]) => {
    // No "noopener" feature: with it, window.open always returns null, and a
    // blocked tab would look like one that opened.
    const tab = window.open(getAiToolUrl(tool.url, prompt), "_blank");
    if (!tab) {
      errorNotificationVar({
        title: `Your browser blocked ${tool.name}`,
        description: "Copy the prompt and paste it into your AI tool instead.",
      });
      return;
    }
    tab.opener = null;
    onDone();
  };

  const sectionHeader = "text-sm font-semibold text-gray-900";
  // Each part ruled off above, the rule running the modal's full width past
  // its padding; its heading and buttons on one line.
  const card = "-mx-6 border-t border-gray-200 px-6 py-6";
  const row = classNames(card, "flex items-center justify-between gap-3");
  return (
    <>
      <div className="-mb-6 flex flex-col">
        <section className={classNames(card, "flex flex-col gap-3")}>
          <div className="flex items-center justify-between gap-3">
            <h4 className={sectionHeader}>Copy a prompt to your AI tool</h4>
            <Button size={ButtonSize.medium} onClick={handleCopy}>
              {copied ? (
                <>
                  <TopicflowIcon
                    type="check"
                    className="mr-1 h-4 w-4 text-emerald-600"
                  />
                  Copied
                </>
              ) : (
                <>
                  <TopicflowIcon type="copy" className="mr-1 h-4 w-4" />
                  Copy prompt
                </>
              )}
            </Button>
          </div>
          <pre className="max-h-60 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs leading-relaxed text-gray-700">
            {prompt}
          </pre>
        </section>

        <section className={row}>
          <h4 className={sectionHeader}>Open it in your AI tool</h4>
          <div className="flex shrink-0 gap-2">
            {AI_TOOLS.map((tool) => (
              <Button
                key={tool.name}
                size={ButtonSize.medium}
                onClick={() => openIn(tool)}
              >
                <img
                  src={getAssetUrl(tool.logo)}
                  alt=""
                  className="mr-1.5 h-4 w-4 object-contain"
                />
                Open in {tool.name}
              </Button>
            ))}
          </div>
        </section>

        <section className={row}>
          <h4 className={sectionHeader}>
            First time using your AI tool with Topicflow?
          </h4>
          <Button
            size={ButtonSize.medium}
            className="shrink-0"
            onClick={() => setMcpSetupOpen(true)}
          >
            Connect your AI tool
          </Button>
        </section>
      </div>
      {/* Outside the divided sections, which would rule it off as one. */}
      <McpIntegrationDialog
        open={mcpSetupOpen}
        onClose={() => setMcpSetupOpen(false)}
      />
    </>
  );
};

/**
 * A card on top of the empty Topicflow AI sidebar of a review, that opens the
 * handoff in a modal of its own. Shaped like the start cards of TF-1709.
 */
const CompleteInAiTool = ({ review }: { review: ReviewForAiTool }) => {
  const label = useLabel();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group relative flex w-full flex-col items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 text-left transition-[border-color,box-shadow] hover:border-gray-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500"
      >
        <span className="flex -space-x-1.5">
          {AI_TOOLS.map((tool) => (
            <span
              key={tool.name}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white"
            >
              <img
                src={getAssetUrl(tool.logo)}
                alt=""
                className="h-5 w-5 object-contain"
              />
            </span>
          ))}
        </span>
        <span className="flex flex-col gap-1 pr-6">
          <span className="text-sm font-semibold text-gray-900">
            Want to complete your {label("review")} in your AI tool?
          </span>
          <span className="text-sm text-gray-600">
            Use your own AI tool to draft answers
          </span>
        </span>
        <TopicflowIcon
          type="arrowRight"
          className="absolute right-4 top-4 h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-600"
        />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} alignedTop>
        <div className="flex flex-col gap-4 p-6">
          <ModalTitle onClose={() => setOpen(false)}>
            Complete your {label("review")} in your AI tool
          </ModalTitle>
          <McpHandoffSection review={review} onDone={() => setOpen(false)} />
        </div>
      </Modal>
    </>
  );
};

export default CompleteInAiTool;
